<template>
  <BaseVeeForm
    :action="submitForm"
    ensure-error-keys-camel-case
    v-slot="{ isPending, invalid }"
    class="space-y-8"
  >
    <div class="space-y-4">
      <ValidationProvider rules="required" name="name" slim vid="name">
        <template #default="{ errors }">
          <BaseFormGroup>
            <BaseFormLabel> Name </BaseFormLabel>
            <BaseFormInlineMessage info>
              A name to easily distinguish the service.
            </BaseFormInlineMessage>
            <BaseFormInput
              type="text"
              v-model="formData.name"
              readonly
              class="w-2/3"
              @focus="$event.target.removeAttribute('readonly')"
            />
            <BaseFormInlineMessage v-if="errors.length" danger>
              {{ errors[0] }}
            </BaseFormInlineMessage>
          </BaseFormGroup>
        </template>
      </ValidationProvider>
      <ValidationProvider rules="required" name="API key" slim vid="apiKey">
        <template #default="{ errors }">
          <BaseFormGroup>
            <BaseFormLabel> API key </BaseFormLabel>
            <BaseFormInlineMessage info> Available on Nexmo dashboard </BaseFormInlineMessage>
            <BaseFormInput
              type="text"
              v-model="formData.apiKey"
              readonly
              class="w-2/3"
              @focus="$event.target.removeAttribute('readonly')"
            />
            <BaseFormInlineMessage v-if="errors.length" danger>
              {{ errors[0] }}
            </BaseFormInlineMessage>
          </BaseFormGroup>
        </template>
      </ValidationProvider>
      <ValidationProvider rules="required" name="API secret" slim vid="apiSecret">
        <template #default="{ errors }">
          <BaseFormGroup>
            <BaseFormLabel> API secret </BaseFormLabel>
            <BaseFormInlineMessage info> Available on Nexmo dashboard </BaseFormInlineMessage>
            <div class="flex space-x-2">
              <BaseFormInput
                :type="showApiSecret ? 'text' : 'password'"
                v-model="formData.apiSecret"
                class="w-2/3"
                @focus="$event.target.removeAttribute('readonly')"
              />
              <BaseButton type="button" primary outline @click="setShowApiSecret(!showApiSecret)">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="icon icon-tabler icon-tabler-eye"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                  stroke="currentColor"
                  fill="none"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <template v-if="showApiSecret">
                    <line x1="3" y1="3" x2="21" y2="21" />
                    <path d="M10.584 10.587a2 2 0 0 0 2.828 2.83" />
                    <path
                      d="M9.363 5.365a9.466 9.466 0 0 1 2.637 -.365c4
                      0 7.333 2.333 10 7c-.778 1.361 -1.612 2.524 -2.503
                      3.488m-2.14 1.861c-1.631 1.1 -3.415 1.651 -5.357
                      1.651c-4 0 -7.333 -2.333 -10 -7c1.369 -2.395 2.913 -4.175 4.632 -5.341"
                    />
                  </template>
                  <template v-else>
                    <circle cx="12" cy="12" r="2" />
                    <path
                      d="M22 12c-2.667 4.667 -6 7 -10 7s-7.333
                      -2.333 -10 -7c2.667 -4.667 6 -7 10 -7s7.333 2.333 10 7"
                    />
                  </template>
                </svg>
              </BaseButton>
            </div>
            <BaseFormInlineMessage v-if="errors.length" danger>
              {{ errors[0] }}
            </BaseFormInlineMessage>
          </BaseFormGroup>
        </template>
      </ValidationProvider>
      <ValidationProvider rules="required" name="sms from" slim vid="smsFrom">
        <template #default="{ errors }">
          <BaseFormGroup>
            <BaseFormLabel> Sms from </BaseFormLabel>
            <BaseFormInlineMessage info>
              Can be a number of up to 15 digits long (including country code without leading zeros)
              or an alphanumeric value (e.g. a Brand name) of up to 11 characters long
            </BaseFormInlineMessage>
            <BaseFormInput type="text" v-model="formData.smsFrom" class="w-2/3" />
            <BaseFormInlineMessage v-if="errors.length" danger>
              {{ errors[0] }}
            </BaseFormInlineMessage>
          </BaseFormGroup>
        </template>
      </ValidationProvider>
    </div>
    <div class="space-x-2">
      <BaseButton primary :disabled="isPending || invalid"> Submit </BaseButton>

      <BaseButton type="button" ghost :disabled="isPending" @click="$emit('cancel')">
        Cancel
      </BaseButton>
    </div>
  </BaseVeeForm>
</template>

<script>
import { pick } from 'lodash-es';
import BaseVeeForm from 'Components/BaseVeeForm.vue';

export default {
  components: { BaseVeeForm },
  props: {
    defaultData: {
      type: Object,
      default: () => ({}),
    },
    action: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      showApiSecret: false,
      formData: {
        name: '',
        apiKey: '',
        apiSecret: '',
        smsFrom: '',
        ...this.getDefaultData(),
      },
    };
  },
  methods: {
    setShowApiSecret(value) {
      this.showApiSecret = Boolean(value);
    },
    getDefaultData() {
      const { config = {} } = this.defaultData || {};

      if (typeof config !== 'object' || config === null) {
        return {};
      }

      return pick(config, ['name', 'apiKey', 'apiSecret', 'smsFrom']);
    },
    async submitForm() {
      try {
        await this.action({ config: this.formData });
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
  },
};
</script>
